import { useQueryClient } from "@tanstack/react-query";
import React from "react";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { type PaginatedResponse } from "@/shared/http";

import { fileQueryKeys } from "./fileQueryKeys";

/** Page size requested per search. Bounded so the caller renders a short list
 *  and the user types to narrow rather than scrolling. */
const SEARCH_LIMIT = 20;

/**
 * Lazy, server-side search over an org's file tags.
 *
 * Returns a stable function that resolves one page of matching tags. Unlike
 * {@link useOrgFileTags}, nothing is fetched until it is called.
 */
export function useOrgFileTagSearch(
  orgId?: string,
): (
  search: string,
  signal?: AbortSignal,
) => Promise<PaginatedResponse<string>> {
  const { currentOrganization } = useAuth();
  const { files: fileService } = useDomainServices();
  const queryClient = useQueryClient();
  const canonicalOrgId = orgId ?? currentOrganization?.org_id ?? "";

  return React.useCallback(
    (search, signal) => {
      const fetchPage = (abortSignal?: AbortSignal) =>
        fileService.getTagsForOrg(
          { search: search || undefined, limit: SEARCH_LIMIT },
          { resourceOwnerId: canonicalOrgId, abortSignal },
        );

      if (search !== "") {
        return fetchPage(signal);
      }

      return queryClient.ensureQueryData({
        queryKey: fileQueryKeys.orgTagSearch(canonicalOrgId, ""),
        queryFn: ({ signal: querySignal }) => fetchPage(querySignal),
      });
    },
    [queryClient, fileService, canonicalOrgId],
  );
}
